// Utilidades de moeda. Formatação via Intl.NumberFormat (nunca float manual).

export interface CurrencyInfo {
  code: string
  name: string
  locale: string
  // fx: tem cotação AUTOMÁTICA (Frankfurter/BCE ou open.er-api).
  fx: boolean
  // historical: tem taxa HISTÓRICA por data (só Frankfurter/BCE).
  // Moedas com historical=false (ex.: GTQ) só têm cotação atual automática;
  // para transações retroativas usa-se a última taxa em cache ou taxa manual.
  historical: boolean
}

// CATÁLOGO de moedas conhecidas (com metadados). As que um household de fato USA
// são as "habilitadas" (households.enabled_currencies) — um subconjunto deste
// catálogo. O backend/Frankfurter+open.er-api lida com ~160 moedas; para incluir
// mais no catálogo, basta adicionar aqui.
export const CURRENCIES: CurrencyInfo[] = [
  { code: 'GTQ', name: 'Quetzal (Guatemala)', locale: 'es-GT', fx: true, historical: false },
  { code: 'USD', name: 'Dólar americano', locale: 'en-US', fx: true, historical: true },
  { code: 'BRL', name: 'Real brasileiro', locale: 'pt-BR', fx: true, historical: true },
  { code: 'EUR', name: 'Euro', locale: 'de-DE', fx: true, historical: true },
  { code: 'GBP', name: 'Libra esterlina', locale: 'en-GB', fx: true, historical: true },
  { code: 'CAD', name: 'Dólar canadense', locale: 'en-CA', fx: true, historical: true },
  { code: 'CHF', name: 'Franco suíço', locale: 'de-CH', fx: true, historical: true },
  { code: 'JPY', name: 'Iene japonês', locale: 'ja-JP', fx: true, historical: true },
  { code: 'AUD', name: 'Dólar australiano', locale: 'en-AU', fx: true, historical: true },
  { code: 'MXN', name: 'Peso mexicano', locale: 'es-MX', fx: true, historical: true },
  { code: 'ARS', name: 'Peso argentino', locale: 'es-AR', fx: true, historical: false },
  { code: 'CLP', name: 'Peso chileno', locale: 'es-CL', fx: true, historical: false },
  { code: 'COP', name: 'Peso colombiano', locale: 'es-CO', fx: true, historical: false },
  { code: 'PEN', name: 'Sol peruano', locale: 'es-PE', fx: true, historical: false },
  { code: 'UYU', name: 'Peso uruguaio', locale: 'es-UY', fx: true, historical: false },
]

export const CURRENCY_CODES = CURRENCIES.map((c) => c.code)

const byCode = new Map(CURRENCIES.map((c) => [c.code, c]))

export function currencyInfo(code: string): CurrencyInfo {
  return byCode.get(code) ?? { code, name: code, locale: 'pt-BR', fx: false, historical: false }
}

// Info das moedas HABILITADAS (na ordem recebida) — usada pelos seletores do app.
export function enabledCurrencyList(codes: string[]): CurrencyInfo[] {
  return codes.map(currencyInfo)
}

export function isFxSupported(code: string): boolean {
  return currencyInfo(code).fx
}

export function hasHistorical(code: string): boolean {
  return currencyInfo(code).historical
}

// Igual a formatCurrency, mas retorna '' (vazio) quando o valor é zero/nulo —
// para não poluir tabelas com "Q 0,00" onde não há valor.
export function formatCurrencyBlank(amount: number | null | undefined, code: string, localeOverride?: string): string {
  if (amount == null || Math.abs(Number(amount)) < 0.005) return ''
  return formatCurrency(amount, code, localeOverride)
}

// Símbolo da moeda (Q, R$, $…), extraído do locale NATIVO dela. Cacheado.
const symbolCache = new Map<string, string>()
export function currencySymbol(code: string): string {
  const hit = symbolCache.get(code)
  if (hit != null) return hit
  let sym = code
  try {
    const parts = new Intl.NumberFormat(currencyInfo(code).locale, {
      style: 'currency',
      currency: code,
    }).formatToParts(0)
    sym = parts.find((p) => p.type === 'currency')?.value ?? code
  } catch {
    /* código desconhecido: usa o próprio code */
  }
  symbolCache.set(code, sym)
  return sym
}

// Números SEMPRE no padrão pt-BR (1.000,00), independentemente da moeda — o
// locale nativo de algumas (ex.: es-GT) usa 1,000.00 e ficava inconsistente.
const NUM_LOCALE = 'pt-BR'

// Formata um valor NUMERIC(14,2): "<símbolo> 1.000,00" (negativo: "-Q 1.000,00").
export function formatCurrency(amount: number | null | undefined, code: string, localeOverride?: string): string {
  if (amount == null || Number.isNaN(amount)) return '—'
  // Evita "-0,00" (negativo de zero) e resíduos de ponto flutuante: snap p/ 0.
  const value = Math.abs(amount) < 0.005 ? 0 : amount
  const num = new Intl.NumberFormat(localeOverride ?? NUM_LOCALE, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(value))
  return `${value < 0 ? '-' : ''}${currencySymbol(code)} ${num}`
}
